import moment from 'moment';
import Rest from '../../../utils/rest/rest';
import { ensureTrailingSlash } from '../../../utils/url/url';
import { closeModal, openModal, Modals } from '..';
import { SessionType } from '..';
import { ConnectionStatus } from '..';
import { publishLocalVideoTrack, unpublishVideoTracks } from '../../../twilio/twilio';
import { AdminCommands } from '../../../twilio/adminCommands';
import { Session } from '../session/session';

const apiUrl = ensureTrailingSlash(process.env.REACT_APP_API_URL);
const apiPrefix = 'api/v1/';

export const ActiveSession = Object.freeze({
  UPDATE_SESSION_TYPE: 'UPDATE_SESSION_TYPE',
  UPDATE_MEMBERS: 'ACTIVE_SESSION_UPDATE_MEMBERS',
  UPDATE_MEMBER: 'ACTIVE_SESSION_UPDATE_MEMBER',
  UPDATE_SCREENS: 'ACTIVE_SESSION_UPDATE_SCREENS',
  UPDATE_ROLES: 'UPDATE_ROLES',
  UPDATE_MESSAGES: 'UPDATE_MESSAGES',
  UPDATE_START_TIME: 'UPDATE_START_TIME',
  UPDATE_INTERPRETATION_TEXT: 'UPDATE_INTERPRETATION_TEXT',
  UPDATE_CHAT_HIDDEN: 'UPDATE_CHAT_HIDDEN',
});

const SCREEN_DEFAULT_COUNT = 1;
const SCREEN_MAX_COUNT = 8;

export const updateStartTime = (time) => ({
  type: ActiveSession.UPDATE_START_TIME,
  payload: time,
});

export const setSessionType = (type) => ({
  type: ActiveSession.UPDATE_SESSION_TYPE,
  payload: type,
});

export const changeSessionType = (type) => async (dispatch, getState) => {
  const { type: oldType } = getState().activeSession;
  const { activeSessionData } = getState().session;
  const { callData: { sessionId } = {} } = activeSessionData || {};
  const changeSessionTypePath = `${apiUrl}${apiPrefix}session/${sessionId}/type/${type}`;
  try {
    await Rest.put(changeSessionTypePath);
    dispatch(AdminCommands.changeSessionType(type));
    dispatch(setSessionType(type));
    if (oldType === SessionType.VIDEO && type === SessionType.TEXT) {
      dispatch(unpublishVideoTracks());
    } else if (oldType === SessionType.TEXT && type === SessionType.VIDEO) {
      const { cameraInUse } = getState().settings;
      dispatch(publishLocalVideoTrack(cameraInUse));
    }
  } catch (error) {
    console.error('Change session type failed', error);
  }
};

export const addNewMember = (participant, isSelf) => {
  return (dispatch, getState) => {
    const name = participant.identity;
    console.log('## addNewMember', name, participant);

    const id = participant.sid;
    const newMember = {
      id,
      name,
      isSelf,
    };

    const oldMembers = getState().activeSession.members;
    if (oldMembers.find((m) => m.id === id)) {
      console.warn(`Participant ${name} added again, clean up does not work.`);
    }
    // if participant is already listed, remove it first
    const members = oldMembers.filter((m) => m.id !== id);
    // add new member
    members.push(newMember);

    dispatch({
      type: ActiveSession.UPDATE_MEMBERS,
      payload: members,
    });

    if (isSelf) {
      participant.on('disconnected', () => {
        dispatch(removeMember(participant));
      });
    }

    const sessionType = getState().activeSession.type;
    if (sessionType === SessionType.VIDEO) {
      dispatch(addMemberToScreen(newMember));
    }

    return Promise.resolve(newMember);
  };
};

export const removeMember = (participant) => {
  return (dispatch, getState) => {
    console.log('removeMember', participant);

    const sessionType = getState().activeSession.type;
    if (sessionType === SessionType.VIDEO) {
      // first remove from screen
      dispatch(removeMemberFromScreen(participant.sid));
    }

    // remove listeners
    participant.removeAllListeners();

    // update list
    const members = getState().activeSession.members.slice(0);
    const index = members.findIndex((listMember) => listMember.id === participant.sid);

    // if found
    if (index !== -1) {
      // remove and update
      members.splice(index, 1);
      dispatch({
        type: ActiveSession.UPDATE_MEMBERS,
        payload: members,
      });
    }
  };
};

export const removeMembers = () => ({
  type: ActiveSession.UPDATE_MEMBERS,
  payload: [],
});

export const addMemberToScreen = (member) => {
  return (dispatch, getState) => {
    console.log('## addMemberToScreen ', member);

    let updatedScreens = getState().activeSession.screens.slice(0);
    let updatedMember = { ...member, screenIndex: null, noAutoUp: false };

    const alreadyOnScreen = updatedScreens.find((s) => s.member && s.member.id === member.id);
    if (alreadyOnScreen) {
      return;
    }

    // find free slot
    const freeSlotIndex = updatedScreens.findIndex((s) => s.reserved === false);
    if (freeSlotIndex !== -1) {
      // found, update
      updatedMember = { ...updatedMember, screenIndex: freeSlotIndex };
      const screenData = { member: updatedMember, reserved: true };

      // update existing screen
      updatedScreens.splice(freeSlotIndex, 1, screenData);
    } else {
      // no space, check if the screens can be expanded
      if (updatedScreens.length < SCREEN_MAX_COUNT) {
        // new slot, create data
        const freeSlotIndex = updatedScreens.length;

        updatedMember = { ...updatedMember, screenIndex: freeSlotIndex };
        const screenData = { member: updatedMember, reserved: true };

        // add new screen
        updatedScreens.push(screenData);
      }
    }

    // update member
    dispatch({ type: ActiveSession.UPDATE_MEMBER, payload: updatedMember });

    // update screens
    dispatch({
      type: ActiveSession.UPDATE_SCREENS,
      payload: updatedScreens,
    });

    return Promise.resolve({ member: updatedMember, screens: updatedScreens });
  };
};

export const removeMemberFromScreen = (id) => {
  return (dispatch, getState) => {
    let updatedScreens = getState().activeSession.screens.slice(0);
    const members = getState().activeSession.members.slice(0);
    let removedMember = members.find((member) => member.id === id);
    removedMember = removedMember && { ...removedMember, screenIndex: null, noAutoUp: true };

    let addedMember = null;

    // find screen
    const screenIndex = updatedScreens.findIndex((screen) => {
      const member = screen.member || {};
      return member.id === id;
    });
    if (screenIndex === -1) {
      return;
    }

    if (screenIndex !== -1) {
      // check if there's users that are not on screen
      const member = members.find((member) => member.screenIndex == null && !member.noAutoUp);
      if (member != null) {
        // update new member
        addedMember = { ...member, screenIndex };

        // replace
        updatedScreens.splice(screenIndex, 1, {
          member: addedMember,
          reserved: true,
        });
      } else {
        // no more users, check if the screen can be removed
        if (updatedScreens.length > SCREEN_DEFAULT_COUNT) {
          // remove screen
          updatedScreens.splice(screenIndex, 1);
        } else {
          // make it empty
          updatedScreens.splice(screenIndex, 1, {
            member: null,
            reserved: false,
          });
        }
      }
    }

    // update removed member
    if (removedMember) {
      dispatch({ type: ActiveSession.UPDATE_MEMBER, payload: removedMember });
    }

    // update added member
    if (addedMember != null) {
      dispatch({ type: ActiveSession.UPDATE_MEMBER, payload: addedMember });
    }

    // update screens
    if (screenIndex !== -1) {
      dispatch({
        type: ActiveSession.UPDATE_SCREENS,
        payload: updatedScreens,
      });
    }

    return Promise.resolve({ removedMember, addedMember, screens: updatedScreens });
  };
};

export const initScreens = (count) => {
  return [...Array(count)].map(() => ({ member: null, reserved: false }));
};

export const clearScreens = () => {
  return (dispatch) => {
    dispatch({ type: ActiveSession.UPDATE_SCREENS, payload: initScreens(4) });
  };
};

export const updateRoles = (roles = {}) => ({
  type: ActiveSession.UPDATE_ROLES,
  payload: roles,
});

export const updateRoleAndScreen = (name, role) => (dispatch, getState) => {
  const { members, roles, type } = getState().activeSession;
  const member = members.find((m) => m.name === name);
  dispatch(updateRoles({ ...roles, [name]: role }));

  const isVideoSession = type === SessionType.VIDEO;
  if (isVideoSession) {
    if (role === 'hears' && member && member.screenIndex !== null) {
      dispatch(removeMemberFromScreen(member.id));
    } else if (role !== 'hears' && member.screenIndex === null) {
      dispatch(addMemberToScreen(member));
    }
  }

  if (member.isSelf) {
    const videoTracks = getState().twilio.room.localParticipant.videoTracks;
    const priority = role === 'hears' ? 'low' : 'standard'; // 'high'
    for (const [, publication] of videoTracks) {
      publication.setPriority(priority);
    }
  }
};

export const changeRole = (name, role) => async (dispatch, getState) => {
  const { activeSessionData } = getState().session;
  const { callData: { sessionId } = {} } = activeSessionData || {};
  if (!sessionId) {
    console.error('No active session');
    return;
  }
  const changeRolePath = `${apiUrl}${apiPrefix}session/role/${sessionId}`;
  try {
    await Rest.put(changeRolePath, { name, role });
    dispatch(AdminCommands.changeParticipantRole(name, role));
    dispatch(updateRoleAndScreen(name, role));
  } catch (error) {
    console.error('Role update failed', error);
  }
};

export const kickParticipant = (userId, name) => async (dispatch, getState) => {
  const { activeSessionData } = getState().session;
  const { callData: { sessionId } = {} } = activeSessionData || {};
  const kickPath = `${apiUrl}${apiPrefix}session/${sessionId}/kick/${userId}`;

  try {
    dispatch(AdminCommands.kickParticipant(name));
    await Rest.put(kickPath);
    dispatch(addStatusMessage('chat.kicked', { name }));
  } catch (error) {
    console.error('Kick user failed', error);
  }
};

let msgCount = 0;

export const addChatMessage = (text, name, channel = 'all') => async (dispatch, getState) => {
  msgCount++;
  const newMsg = {
    id: msgCount,
    channel,
    isSelf: !name,
    isNotification: false,
    name,
    text,
    timestamp: moment(),
    isSeen: false,
  };
  const messages = getState().activeSession.messages;
  dispatch({
    type: ActiveSession.UPDATE_MESSAGES,
    payload: [...messages, newMsg],
  });
};

export const addStatusMessage = (i18nKey, params) => async (dispatch, getState) => {
  msgCount++;
  const newMsg = {
    id: msgCount,
    channel: 'all',
    isNotification: true,
    i18nKey,
    params,
    timestamp: moment(),
    isSeen: false,
  };
  const messages = getState().activeSession.messages;
  dispatch({
    type: ActiveSession.UPDATE_MESSAGES,
    payload: [...messages, newMsg],
  });
};

export const handleRoomDisconnected = (room, error, reconnectionHandler) => async (
  dispatch,
  getState
) => {
  const { status, room: currentRoom } = getState().twilio;
  console.log('Roooom disconnected', error, currentRoom, room);

  // 53118: TwilioError: Room completed
  if (error && error.code === 53118) {
    dispatch(
      openModal(
        {
          headerText: { i18nKey: 'modal.disconnected.header' },
          bodyText: { i18nKey: 'chat.sessionEnded' },
          leftButtonText: null,
          rightButtonText: null,
        },
        Modals.CONNECTION_ERROR
      )
    );
    return;
  }

  if (status === ConnectionStatus.CONNECTED) {
    dispatch(addStatusMessage('chat.disconnected'));
    const bodyText = !error
      ? { i18nKey: 'modal.disconnected.body' }
      : `${error.code}: ${error.toString()}`;
    dispatch(
      openModal(
        {
          headerText: { i18nKey: 'modal.disconnected.header' },
          bodyText,
          leftButtonText: null,
          rightButtonText: { i18nKey: 'modal.disconnected.reconnect' },
          onRightButtonClick: () => {
            dispatch(closeModal());
            reconnectionHandler();
          },
          onClose: () => {},
        },
        Modals.CONNECTION_ERROR
      )
    );
    return;
  } else if (status === ConnectionStatus.KICKED) {
    dispatch(
      openModal(
        {
          headerText: { i18nKey: 'modal.kicked.header' },
          bodyText: { i18nKey: 'modal.kicked.body' },
          leftButtonText: null,
          rightButtonText: null,
          onClose: () => {},
        },
        Modals.CONNECTION_ERROR
      )
    );
    return;
  }
};

export const updateInterpretationText = (name, text, version) => (dispatch, getState) => {
  const {
    text: { version: currentVersion },
    roles,
  } = getState().activeSession;

  if (version && version <= currentVersion) {
    // multiple interpreters write same time
    // or sync after reconnection
    return;
  }

  if (!name) {
    if (currentVersion === 0) {
      // allow (re)connection sync from all users
      dispatch({
        type: ActiveSession.UPDATE_INTERPRETATION_TEXT,
        payload: {
          text,
          version: version || currentVersion + 1,
        },
      });
    }
    return;
  }

  const updaterRole = roles[name];
  if (updaterRole !== 'interpreter') {
    console.warn(`Received text from user ${name} with role ${updaterRole}.`);
    return;
  }

  dispatch({
    type: ActiveSession.UPDATE_INTERPRETATION_TEXT,
    payload: {
      text,
      lastWriter: name,
      lastChangeDate: +new Date(),
      version: version || currentVersion + 1,
    },
  });
};

export const setChatHidden = (hidden = true) => ({
  type: ActiveSession.UPDATE_CHAT_HIDDEN,
  payload: hidden,
});

const INIT_STATE = {
  type: null,
  members: [],
  screens: initScreens(2),
  roles: {},
  messages: [],
  startTime: null,
  text: { text: '', lastWriter: null, lastChangeDate: null, version: 0 },
  isChatHidden: false,
};

export const activeSessionReducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case Session.INIT_SESSION_STARTED: {
      return { ...INIT_STATE };
    }
    case ActiveSession.UPDATE_SESSION_TYPE: {
      return { ...state, type: action.payload };
    }
    case ActiveSession.UPDATE_MEMBERS: {
      // console.log('REDUX: Update members', action.payload);
      return { ...state, members: action.payload };
    }
    case ActiveSession.UPDATE_MEMBER: {
      const member = action.payload;
      const members = state.members.slice(0);
      const index = members.findIndex((listMember) => listMember.id === member.id);
      members.splice(index, 1, member);
      return { ...state, members };
    }
    case ActiveSession.UPDATE_SCREENS: {
      return { ...state, screens: action.payload };
    }
    case ActiveSession.UPDATE_ROLES: {
      return { ...state, roles: action.payload };
    }
    case ActiveSession.UPDATE_MESSAGES: {
      return { ...state, messages: action.payload };
    }
    case ActiveSession.UPDATE_START_TIME: {
      return { ...state, startTime: action.payload };
    }
    case ActiveSession.UPDATE_INTERPRETATION_TEXT: {
      return { ...state, text: action.payload };
    }
    case ActiveSession.UPDATE_CHAT_HIDDEN: {
      return { ...state, isChatHidden: action.payload };
    }
    default:
      return state;
  }
};
